import {
  useGetAvailableContractTrackingWorkflowsSuspense,
  useCreateContractTracking,
  getGetContractTrackingWorkflowStepsQueryKey,
  useStartSigning,
  useUploadContract,
} from "@/orval/gen"
import { getDateString } from "@/routes/app/-components/date-time"
import { useInvalidate } from "@/typed-client/hooks/use-invalidate"
import { showErrorNotification } from "@/utils/form-submission-notifications"
import { CancelButton, SubmitButton } from "@components/Buttons"
import { DefaultModal } from "@components/default-modal"
import { FormButtonGroup } from "@components/form-button-group"
import {
  ModalProps,
  Modal,
  Stack,
  Radio,
  Textarea,
  LoadingOverlay,
  Button,
  Text,
  Group,
} from "@mantine/core"
import { DateInput } from "@mantine/dates"
import { useForm } from "@mantine/form"
import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"

interface Props extends ModalProps {
  contractId: number
  defaultStatusId?: number
}
function ApproveContractModalContent({
  contractId,
  defaultStatusId,
  ...modalProps
}: Props) {
  const { data: workflows, queryKey } =
    useGetAvailableContractTrackingWorkflowsSuspense({
      contractId,
    })

  const workflowStepsQueryKey = getGetContractTrackingWorkflowStepsQueryKey({
    contractId,
  })

  const queryClient = useQueryClient()

  const form = useForm({
    mode: "uncontrolled",
    initialValues: {
      approvalStatusId:
        defaultStatusId ??
        workflows.at(0)?.contractApprovalStatus?.contractApprovalStatusId,
      note: undefined,
      trackingDate: getDateString(),
    },
  })

  React.useEffect(() => {
    if (
      workflows.some(
        (w) =>
          w.contractApprovalStatus.contractApprovalStatusId ===
          form.getValues().approvalStatusId
      )
    ) {
      return
    }
    form.setFieldValue(
      "approvalStatusId",
      workflows.at(0)?.contractApprovalStatus?.contractApprovalStatusId
    )
  }, [workflows])

  React.useEffect(() => {
    if (defaultStatusId) {
      form.setFieldValue("approvalStatusId", defaultStatusId)
    }
  }, [defaultStatusId])

  const invalidate = useInvalidate()

  const invalidateContractDetails = React.useCallback(() => {
    invalidate("/api/Contracts/GetContractDetails", { id: contractId })
    queryClient.invalidateQueries({ queryKey })
    queryClient.invalidateQueries({ queryKey: workflowStepsQueryKey })
  }, [queryClient, queryKey, workflowStepsQueryKey, invalidate, contractId])

  const { mutate: createTracking, isPending: createTrackingIsPending } =
    useCreateContractTracking({
      mutation: {
        onError: showErrorNotification,
        onSuccess: () => {
          invalidateContractDetails()
          modalProps.onClose()
          form.initialized = false
        },
      },
    })

  const { mutate: addSigner, isPending: addSignerIsPending } = useStartSigning({
    mutation: {
      onError: showErrorNotification,
      onSuccess: (url) => {
        location.href = url
      },
    },
  })

  const selectedWorkflow = workflows.find(
    (w) =>
      w.contractApprovalStatus.contractApprovalStatusId ===
      form.getValues().approvalStatusId
  )

  const isSubmitting = createTrackingIsPending || addSignerIsPending

  const uploadRequired =
    selectedWorkflow?.signatureType && !selectedWorkflow?.envelopeId

  const { mutateAsync: uploadContract, isPending: uploadContractIsPending } =
    useUploadContract({
      mutation: {
        onSuccess: () => queryClient.invalidateQueries({ queryKey }),
        onError: showErrorNotification,
      },
    })

  const handleUpload = async () => {
    await uploadContract({ data: contractId })
  }

  const handleSubmit = (formValues: typeof form.values) => {
    if (!formValues.approvalStatusId) return
    const workflow = workflows.find(
      (w) =>
        w.contractApprovalStatus.contractApprovalStatusId ===
        formValues.approvalStatusId
    )
    if (!workflow) return

    if (workflow.signatureType === "Internal") {
      const { envelopeId } = workflow
      if (!envelopeId) return
      addSigner({
        data: { envelopeId, contractId, workflowId: workflow.workflowId },
      })
    } else {
      createTracking({
        data: {
          ...formValues,
          contractId,
          approvalStatusId: formValues.approvalStatusId,
        },
      })
    }
  }

  const { t } = useTranslation("app_server")

  const radios = React.useMemo(
    () =>
      workflows.map((status) => {
        const { contractApprovalStatusId: statusId, name } =
          status.contractApprovalStatus
        return (
          <Radio
            my={"xs"}
            key={statusId}
            value={statusId.toString()}
            label={name}
            checked={form.getValues().approvalStatusId === statusId}
            onChange={() => form.setFieldValue("approvalStatusId", statusId)}
          />
        )
      }),
    [workflows, form]
  )

  return (
    <>
      <LoadingOverlay visible={isSubmitting || uploadContractIsPending} />
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <Radio.Group
            my={"xs"}
            value={form.getValues().approvalStatusId?.toString()}
            label={t("Status")}
          >
            {radios}
          </Radio.Group>
          <DateInput
            valueFormat="L"
            maxDate={new Date()}
            key={form.key("trackingDate")}
            {...form.getInputProps("trackingDate")}
          />
          <Textarea
            label={t("Note")}
            maxLength={250}
            key={form.key("note")}
            {...form.getInputProps("note")}
          />
          {uploadRequired && (
            <Group>
              <Text c="red">{t("ContractApproval_UploadRequired")}</Text>
              <Button size="compact-md" onClick={handleUpload}>
                {t("Upload")}
              </Button>
            </Group>
          )}
          <FormButtonGroup>
            <CancelButton onClick={modalProps.onClose} />
            <SubmitButton disabled={!!uploadRequired}>
              {selectedWorkflow?.signatureType === "Internal"
                ? t("Sign")
                : t("Confirm")}
            </SubmitButton>
          </FormButtonGroup>
        </Stack>
      </form>
    </>
  )
}

export function ApproveContractModal(props: Props) {
  const { t } = useTranslation("app_server")
  return (
    <DefaultModal closeOnEscape title={t("SetTrackingStatus")} {...props}>
      <ApproveContractModalContent {...props} />
    </DefaultModal>
  )
}
